/**
 * Auth Service
 *
 * Handles internal portal authentication with httpOnly cookies.
 *
 * Internal portal flow:
 *   - Access token  → httpOnly cookie, never exposed to JS.
 *   - Refresh token → httpOnly cookie, scoped to /api/v1/auth/.
 *   - User profile  → cached locally for UX only; authentication truth comes
 *                     from /api/v1/auth/me/ and /api/v1/auth/refresh/.
 */

import type {
  User,
  LoginCredentials,
  AccessRoleClaim,
  CredentialLoginInput,
} from './types';
import { deriveApiBaseUrl } from '@/shared/services/apiBase';
import { apiClient, setCachedCsrfToken } from '@/shared/services/api';
import { getImpersonationToken } from './impersonation';
import i18n from '@/core/i18n/i18n';

const API_BASE = deriveApiBaseUrl();
const IS_NGROK = API_BASE.includes('ngrok');
const NGROK_HEADER = IS_NGROK ? { 'ngrok-skip-browser-warning': 'true' } : {};

// Phase 2I (GDPR R12) — what we cache in localStorage.
//
// Until 2I, this key carried the full ``User`` object including
// ``email``, ``name``, ``avatar`` and the entire ``accessRoles`` tree.
// All of that is PII and an XSS could read it directly. Trim to the
// absolute minimum needed before /me/ resolves: { id, companyId }.
// Even those two are technically pseudonymous and not strictly
// necessary (every restoreSession call refetches /me/) — we keep them
// only so a re-render between page-mount and /me/ resolution can
// short-circuit guards that need a userId without flicker.
const USER_KEY = 'shaka_user';
// Legacy localStorage key used pre-2I. Cleared on boot so existing
// users stop carrying cached PII forward.
const LEGACY_USER_KEY_VARIANTS = ['shaka_user'];

export interface CachedUserSentinel {
  id: string;
  companyId: string | null;
}

export interface AuthSession {
  user: User;
}

/**
 * Phase 2I — clear any legacy ``shaka_user`` entry that carries the
 * pre-trim PII shape. Called on boot from ``AuthContext``.
 *
 * The check looks for keys we no longer cache (``email`` /
 * ``accessRoles``). When present, we wipe the entry — the next
 * ``/me/`` resolution refills the in-memory user.
 */
export function purgeLegacyCachedUser(): void {
  for (const key of LEGACY_USER_KEY_VARIANTS) {
    const raw = localStorage.getItem(key);
    if (!raw) continue;
    try {
      const parsed = JSON.parse(raw);
      const looksLegacy =
        parsed && typeof parsed === 'object'
        && (
          Object.prototype.hasOwnProperty.call(parsed, 'email')
          || Object.prototype.hasOwnProperty.call(parsed, 'accessRoles')
          || Object.prototype.hasOwnProperty.call(parsed, 'name')
        );
      if (looksLegacy) {
        localStorage.removeItem(key);
      }
    } catch {
      // Malformed JSON — wipe it.
      localStorage.removeItem(key);
    }
  }
}

interface RawRolePermissionLocation {
  id: string;
  location_id?: string;
  locationId?: string;
}

interface RawRolePermission {
  id: string;
  role: string;
  permission: string;
  permission_detail?: AccessRoleClaim['rolePermissions'][number]['permissionDetail'];
  permissionDetail?: AccessRoleClaim['rolePermissions'][number]['permissionDetail'];
  scope_strategy?: AccessRoleClaim['rolePermissions'][number]['scopeStrategy'];
  scopeStrategy?: AccessRoleClaim['rolePermissions'][number]['scopeStrategy'];
  locations?: RawRolePermissionLocation[];
}

interface RawAccessRole {
  scope_type?: AccessRoleClaim['scopeType'];
  scopeType?: AccessRoleClaim['scopeType'];
  location_id?: string | null;
  locationId?: string | null;
  role_id?: string;
  roleId?: string;
  role_name?: string;
  roleName?: string;
  role_permissions?: RawRolePermission[];
  rolePermissions?: RawRolePermission[];
}

interface RawMe {
  id: string;
  email: string;
  name: string;
  avatar_url?: string;
  avatarUrl?: string;
  company_schema?: string;
  companySchema?: string;
  company_access?: boolean;
  companyAccess?: boolean;
  all_location_access?: boolean;
  allLocationAccess?: boolean;
  access_roles?: RawAccessRole[];
  accessRoles?: RawAccessRole[];
  person_id?: string | null;
  personId?: string | null;
  tenant_memberships?: RawTenantMembership[];
  tenantMemberships?: RawTenantMembership[];
}

interface RawTenantMembership {
  company_schema?: string;
  companySchema?: string;
  company_name?: string;
  companyName?: string;
  is_default?: boolean;
  isDefault?: boolean;
  is_active_tenant?: boolean;
  isActiveTenant?: boolean;
}

function readCachedSentinel(): CachedUserSentinel | null {
  const cachedStr = localStorage.getItem(USER_KEY);
  if (!cachedStr) return null;
  try {
    const parsed = JSON.parse(cachedStr);
    // Phase 2I: only accept the trimmed shape. Anything that looks
    // legacy is treated as absent and wiped, so a stale PII payload
    // can't be revived by a downgrade.
    if (
      !parsed
      || typeof parsed !== 'object'
      || Object.prototype.hasOwnProperty.call(parsed, 'email')
      || Object.prototype.hasOwnProperty.call(parsed, 'accessRoles')
      || Object.prototype.hasOwnProperty.call(parsed, 'name')
    ) {
      localStorage.removeItem(USER_KEY);
      return null;
    }
    return {
      id: String(parsed.id || ''),
      companyId: parsed.companyId ?? null,
    };
  } catch {
    return null;
  }
}

function writeCachedSentinel(user: User | null): void {
  if (!user) {
    localStorage.removeItem(USER_KEY);
    return;
  }
  // PII-free sentinel: id + companyId only. Email / name / accessRoles
  // are NEVER persisted to localStorage — they live in React state and
  // come from /api/v1/auth/me/ on every page load.
  const sentinel: CachedUserSentinel = {
    id: user.id,
    companyId: user.companyId ?? null,
  };
  localStorage.setItem(USER_KEY, JSON.stringify(sentinel));
}

async function fetchCurrentUser(): Promise<User> {
  // /me/ is a raw fetch (not the apiClient), so the impersonation
  // Bearer wiring lives here too. CookieJWTAuthentication on the
  // backend checks the Authorization header before the cookie, so when
  // an impersonation token is active we send it and omit cookies — a
  // fresh browser (e.g. Safari launched by the admin shell) has no
  // cookie to fall back on, and we don't want a leftover operator
  // cookie shadowing the impersonation session either.
  const impersonationToken = getImpersonationToken();
  const headers: Record<string, string> = { ...NGROK_HEADER };
  if (impersonationToken) {
    headers.Authorization = `Bearer ${impersonationToken}`;
  }
  const meRes = await fetch(`${API_BASE}/api/v1/auth/me/`, {
    credentials: impersonationToken ? 'omit' : 'include',
    headers,
  });

  if (!meRes.ok) {
    // Same typed-403 path as the login flow: the tenant got suspended
    // after the JWT was issued. Surface a code so the caller can route
    // to /account-suspended instead of showing a generic auth error.
    const body = await meRes.json().catch(() => ({} as Record<string, unknown>));
    const code = (body as { code?: string }).code
      ?? (body as { detail?: { code?: string } }).detail?.code;
    if (meRes.status === 403 && code === 'tenant_suspended') {
      const err = new Error('tenant_suspended') as Error & { code?: string };
      err.code = 'tenant_suspended';
      throw err;
    }
    throw new Error(i18n.t('auth.errors.fetchProfile', 'Could not fetch user profile'));
  }

  const me = await meRes.json() as RawMe & { csrf_token?: string; csrfToken?: string };
  // Cross-origin SPAs cache the CSRF token from the body since they
  // cannot read the api-origin cookie via document.cookie.
  setCachedCsrfToken(me.csrfToken);
  return buildUser(me);
}

function buildUser(me: RawMe): User {
  const rawAccessRoles = me.accessRoles ?? [];
  const accessRoles: AccessRoleClaim[] = rawAccessRoles.map((ar) => ({
    scopeType: ar.scopeType,
    locationId: ar.locationId ?? null,
    roleId: ar.roleId,
    roleName: ar.roleName,
    rolePermissions: (ar.rolePermissions ?? []).map((rp) => ({
      id: rp.id,
      role: rp.role,
      permission: rp.permission,
      permissionDetail: rp.permissionDetail,
      scopeStrategy: rp.scopeStrategy,
      locations: (rp.locations ?? []).map((loc) => ({
        id: loc.id,
        locationId: loc.locationId,
      })),
    })),
  }));

  return {
    id: me.id,
    email: me.email,
    name: me.name,
    avatar: me.avatarUrl ?? undefined,
    companyId: me.companySchema,
    companyAccess: me.companyAccess ?? false,
    allLocationAccess: me.allLocationAccess ?? false,
    accessRoles,
    personId: me.personId ?? null,
    // Absent on an older backend → undefined, not []. "We don't know" and
    // "exactly one company" are different, and only the second should make
    // the switcher disappear for certain.
    tenantMemberships: (me.tenantMemberships ?? me.tenant_memberships)?.map((m) => ({
      companySchema: (m.companySchema ?? m.company_schema) ?? '',
      companyName: (m.companyName ?? m.company_name) ?? '',
      isDefault: (m.isDefault ?? m.is_default) ?? false,
      isActiveTenant: (m.isActiveTenant ?? m.is_active_tenant) ?? false,
    })),
  };
}

export const authService = {
  async login(credentials: LoginCredentials): Promise<AuthSession> {
    const res = await fetch(`${API_BASE}/api/v1/auth/login/`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...NGROK_HEADER,
      },
      // Normalize the email before it leaves the client: the backend
      // login resolves the account case-insensitively, but sending a
      // trimmed/lowercased address keeps a stray capital or copy-paste
      // whitespace from ever reaching the wire in the first place.
      body: JSON.stringify({ email: credentials.email.trim().toLowerCase(), password: credentials.password }),
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      // Tenant-suspended is a typed 403 from shaka-api. Surface it as
      // a structured error so the LoginPage can route to the dedicated
      // /account-suspended editorial page instead of dropping the
      // operator at the gateway with an empty user payload.
      const code = (body as { code?: string }).code
        ?? (body as { detail?: { code?: string } }).detail?.code;
      if (res.status === 403 && code === 'tenant_suspended') {
        const err = new Error('tenant_suspended') as Error & { code?: string };
        err.code = 'tenant_suspended';
        throw err;
      }
      throw new Error(body.detail || i18n.t('auth.errors.invalidCredentials', 'Invalid credentials'));
    }

    // Login response body carries the CSRF token for cross-origin SPAs.
    const loginBody = await res.json().catch(() => ({} as Record<string, unknown>));
    const csrfFromLogin = (loginBody as { csrf_token?: string; csrfToken?: string }).csrf_token
      ?? (loginBody as { csrfToken?: string }).csrfToken;
    setCachedCsrfToken(csrfFromLogin);

    const user = await fetchCurrentUser();
    writeCachedSentinel(user);
    return { user };
  },

  async loginWithCredential(input: CredentialLoginInput): Promise<AuthSession> {
    const res = await fetch(`${API_BASE}/api/v1/auth/credential-login/`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...NGROK_HEADER,
      },
      body: JSON.stringify({
        credential_type: input.credentialType,
        assertion: {
          transport: input.assertion.transport,
          identifier_kind: input.assertion.identifierKind,
          identifier: input.assertion.identifier,
          security_profile: input.assertion.securityProfile,
          technology: input.assertion.technology ?? '',
          payload: input.assertion.payload ?? '',
          read_at: input.assertion.readAt,
        },
      }),
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.detail || i18n.t('auth.errors.wristbandLoginFailed', 'Could not sign in with the wristband'));
    }

    const user = await fetchCurrentUser();
    writeCachedSentinel(user);
    return { user };
  },

  /**
   * Try the active access cookie first; if expired, refresh via cookie and re-read /me.
   */
  async restoreSession(): Promise<AuthSession | null> {
    try {
      const user = await fetchCurrentUser();
      writeCachedSentinel(user);
      return { user };
    } catch {
      // fall through to refresh
    }

    try {
      const refreshed = await fetch(`${API_BASE}/api/v1/auth/refresh/`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...NGROK_HEADER },
      });

      if (!refreshed.ok) {
        writeCachedSentinel(null);
        return null;
      }

      const user = await fetchCurrentUser();
      writeCachedSentinel(user);
      return { user };
    } catch {
      writeCachedSentinel(null);
      return null;
    }
  },

  async refreshSession(): Promise<boolean> {
    const res = await fetch(`${API_BASE}/api/v1/auth/refresh/`, {
      method: 'POST',
      credentials: 'include',
      headers: { ...NGROK_HEADER },
    });

    if (res.ok) {
      // Refresh response carries the rotated CSRF token; cache it so
      // the next unsafe request adds the right X-CSRFToken header.
      const body = await res.json().catch(() => ({} as Record<string, unknown>));
      const csrf = (body as { csrf_token?: string; csrfToken?: string }).csrf_token
        ?? (body as { csrfToken?: string }).csrfToken;
      setCachedCsrfToken(csrf);
    }
    return res.ok;
  },

  async syncCurrentUser(): Promise<User> {
    const user = await fetchCurrentUser();
    writeCachedSentinel(user);
    return user;
  },

  /**
   * Phase 2I: returns the trimmed sentinel ({ id, companyId }) — NOT
   * the full ``User`` object anymore. PII (email, name, accessRoles)
   * is never persisted to localStorage. Callers that need the full
   * User must call ``syncCurrentUser()`` (which fetches /auth/me/).
   */
  getCachedSentinel(): CachedUserSentinel | null {
    return readCachedSentinel();
  },

  clearCachedUser(): void {
    writeCachedSentinel(null);
  },

  /** POST /api/v1/auth/logout/ — clears the httpOnly refresh cookie. */
  /**
   * Change the ACTIVE company (#14).
   *
   * The server re-issues the auth cookies with the new tenant claim: the old
   * token still carries the old one, so replacing it is the point — not a
   * side effect. The caller must then re-bootstrap (a full reload is the
   * honest move) because every cached query belongs to the company just left.
   */
  async switchTenant(companySchema: string): Promise<void> {
    // Through apiClient, not raw fetch: this is an unsafe method on a
    // cookie-authenticated session, and apiClient is what attaches the
    // X-CSRFToken header. A bare fetch here would 403 on CSRF alone.
    await apiClient.post('/api/v1/auth/switch-tenant/', {
      company_schema: companySchema,
    });
    // The cached sentinel names the company just left.
    writeCachedSentinel(null);
  },

  async logout(): Promise<void> {
    try {
      await fetch(`${API_BASE}/api/v1/auth/logout/`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...NGROK_HEADER },
      });
    } catch {
      // Best-effort — clear local state regardless.
    }
    writeCachedSentinel(null);
  },
};
